import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Sparkles, Check, ArrowLeft, Calendar, Users, 
  MessageCircle, Trophy, Star, Video, Heart, Clock, Shield, Hand, GraduationCap
} from 'lucide-react';

const AlignmentLabLandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-stone-50 relative overflow-hidden" data-testid="alignment-lab-landing">
      {/* Back to Home / Pricing */}
      <Link 
        to="/pricing" 
        className="fixed top-6 left-6 z-20 flex items-center gap-2 text-stone-600 hover:text-primary transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-medium">Back to Pricing</span>
      </Link>

      {/* Decorative blobs */}
      <div className="blob blob-purple w-[500px] h-[500px] -top-64 -right-64 fixed opacity-20" />
      <div className="blob blob-turquoise w-[400px] h-[400px] -bottom-48 -left-48 fixed opacity-20" />

      <div className="max-w-4xl mx-auto px-4 py-20 relative z-10 space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 bg-violet-500/10 text-violet-600 px-4 py-2 rounded-full font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            30-Day Transformation Program
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-4">
            30-Day{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-500 to-purple-600">
              Alignment Lab
            </span>
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto mb-3">
            A 4-week guided realignment for women ready to choose themselves without guilt
          </p>
          <p className="text-stone-500 max-w-2xl mx-auto mb-6">
            Practice rest, stronger boundaries, and deeper self-trust through small weekly experiments, live support, and a community that gets it.
          </p>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
            <Calendar className="w-4 h-4" />
            Starts April 20, 2026
          </div>
        </div>

        {/* This is for you if... */}
        <div className="card-cosmic p-8">
          <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">This is for you if...</h2>
          <ul className="space-y-4 max-w-2xl mx-auto">
            <li className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
              <span className="text-stone-700">You are good at taking care of everything except yourself</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
              <span className="text-stone-700">You look capable on the outside but feel tired, stretched, or disconnected on the inside</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
              <span className="text-stone-700">You keep telling yourself you'll slow down, set boundaries, or choose yourself later</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
              <span className="text-stone-700">Guilt shows up the moment you try to make space for your own needs</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
              <span className="text-stone-700">You are ready for change, but not through pressure, perfectionism, or pretending</span>
            </li>
          </ul>
        </div>

        {/* You are not broken */}
        <div className="card-cosmic p-8">
          <div className="max-w-2xl mx-auto space-y-4 text-stone-700 text-center">
            <p className="text-xl font-semibold text-stone-900">You are not broken. You may simply be out of alignment.</p>
            <p>Maybe you have been holding a lot for a long time.</p>
            <p>Maybe you are capable, caring, responsible, and deeply tired.</p>
            <p>Maybe you keep telling yourself that once things calm down, you'll finally make space for yourself.</p>
            <p className="font-medium text-stone-800">But later keeps becoming later.</p>
            <p>The Alignment Lab is a place to interrupt that pattern.</p>
            <p>Not through force.</p>
            <p>Not through fixing yourself.</p>
            <p>Through honest awareness, small experiments, supportive reflection, and the practice of choosing yourself in ways that actually fit real life.</p>
          </div>
        </div>

        {/* This is not another challenge */}
        <div className="card-cosmic p-8">
          <div className="max-w-2xl mx-auto space-y-4 text-stone-700 text-center">
            <p>This is not another challenge to perform your way through.</p>
            <p>This is not about becoming more disciplined, getting your life perfect, or finally forcing yourself into better habits.</p>
            <p className="font-medium text-stone-800">This is about practicing alignment in real life.</p>
            <p>Inside the Lab, you'll work with small experiments, supportive reflection, and honest shifts that help you create more space, more clarity, and more trust in yourself.</p>
          </div>
        </div>

        {/* Your Weekly Rhythm */}
        <div className="card-cosmic p-8">
          <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">Your Weekly Rhythm</h2>
          <p className="text-center text-stone-600 mb-6">Starting Monday, April 20th</p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-violet-50 rounded-2xl">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-violet-500 flex items-center justify-center">
                <Video className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Monday</h3>
              <p className="text-stone-600">Alignment Experiment</p>
            </div>
            <div className="text-center p-6 bg-fuchsia-50 rounded-2xl">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-fuchsia-500 flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Wednesday</h3>
              <p className="text-stone-600">Live Group Check-in (6 PM ET)</p>
            </div>
            <div className="text-center p-6 bg-amber-50 rounded-2xl">
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-amber-500 flex items-center justify-center">
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2">Friday</h3>
              <p className="text-stone-600">Community Reflection Thread</p>
            </div>
          </div>
        </div>

        {/* Your 4-Week Journey */}
        <div className="card-cosmic p-8">
          <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">Your 4-Week Journey</h2>
          <p className="text-center text-stone-600 max-w-2xl mx-auto mb-4">
            Your 4-week guided reset to help you practice rest, clearer boundaries, healthier availability, and the kind of no that brings you back to yourself.
          </p>
          <p className="text-center text-stone-500 mb-6">Content unlocks weekly as you progress through the lab</p>
          <div className="space-y-4">
            <div className="p-5 rounded-2xl bg-rose-50 border border-rose-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-rose-500 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-sm text-stone-500">Week 1</span>
                  <h3 className="font-bold text-stone-900">Rest is Worth</h3>
                  <p className="text-stone-600 text-sm font-medium">Experiment: The 15-Minute First Yes</p>
                  <p className="text-stone-500 text-sm mt-1">Learn to stop treating rest like a reward and start relating to it as something you are worthy of now.</p>
                </div>
              </div>
            </div>
            <div className="p-5 rounded-2xl bg-blue-50 border border-blue-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-sm text-stone-500">Week 2</span>
                  <h3 className="font-bold text-stone-900">Time Boundaries That Hold</h3>
                  <p className="text-stone-600 text-sm font-medium">Experiment: The Time Fence</p>
                  <p className="text-stone-500 text-sm mt-1">Create realistic boundaries around your time so your energy stops leaking out everywhere.</p>
                </div>
              </div>
            </div>
            <div className="p-5 rounded-2xl bg-purple-50 border border-purple-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-sm text-stone-500">Week 3</span>
                  <h3 className="font-bold text-stone-900">Availability Is Not Love</h3>
                  <p className="text-stone-600 text-sm font-medium">Experiment: The Availability Reset</p>
                  <p className="text-stone-500 text-sm mt-1">Untangle your worth from always being reachable, responsive, and endlessly open.</p>
                </div>
              </div>
            </div>
            <div className="p-5 rounded-2xl bg-amber-50 border border-amber-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                  <Hand className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-sm text-stone-500">Week 4</span>
                  <h3 className="font-bold text-stone-900">The Clean No</h3>
                  <p className="text-stone-600 text-sm font-medium">Experiment: The Clean No</p>
                  <p className="text-stone-500 text-sm mt-1">Practice honest, grounded boundaries without over-explaining, guilt-spiraling, or self-betrayal.</p>
                </div>
              </div>
            </div>
            {/* Alumni Lounge */}
            <div className="p-5 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-sm text-amber-600 font-medium">After Week 4 · Lifetime Access</span>
                  <h3 className="font-bold text-stone-900">Alumni Lounge</h3>
                  <p className="text-stone-600 text-sm">Stay connected, keep practicing, rejoin live waves anytime</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* By the end of these 30 days */}
        <div className="card-cosmic p-8">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-stone-700 mb-4">By the end of these 30 days, you may not have your whole life figured out.</p>
            <p className="text-lg font-semibold text-stone-900 mb-4">But you will have something powerful:</p>
            <ul className="space-y-3 text-left max-w-md mx-auto">
              <li className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                <span className="text-stone-700">More awareness of what drains and supports you</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                <span className="text-stone-700">More clarity about where you've been abandoning yourself</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                <span className="text-stone-700">Practical ways to choose yourself with less guilt</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                <span className="text-stone-700">Stronger trust in your own knowing</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                <span className="text-stone-700">Real momentum toward a life that feels more like yours</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Pricing CTA */}
        <div className="card-cosmic p-8 md:p-12 text-center">
          <div className="mb-6">
            <span className="text-5xl font-bold text-stone-900">$222</span>
            <span className="text-stone-500 ml-2">one-time payment · Lifetime access</span>
          </div>
          
          {isAuthenticated ? (
            <Button
              onClick={() => navigate('/dashboard/alignment-lab')}
              className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-10 py-6 rounded-full text-lg"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Join the Alignment Lab
            </Button>
          ) : (
            <div className="space-y-4">
              <Button
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white px-10 py-6 rounded-full text-lg"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Create Free Account to Enroll
              </Button>
              <p className="text-stone-500 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </div>
          )}
        </div>

        {/* Note about free account */}
        {!isAuthenticated && (
          <div className="card-cosmic p-6 bg-violet-50 border border-violet-200">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                <Star className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Free Account Required</h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  To sign up for the 30-Day Alignment Lab, you'll need to create a free account first. 
                  This gives you access to view the full program details, enroll in the lab, and access 
                  all of the free membership content while you're at it.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Questions */}
        <div className="text-center">
          <p className="text-stone-500 text-sm">
            Questions about the program?{' '}
            <a href="mailto:info@heatherstewart.coach" className="text-primary hover:underline">
              Email Heather
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AlignmentLabLandingPage;
